/**
 * BN product page stock / dispatch / delivery panel (Empire theme).
 *
 * Markup: snippets/bn-pdp-fulfilment.liquid. Rules: pdp-fulfilment-core.js,
 * shared with the ET theme and loaded just before this file.
 *
 * Keeps the panel in step with the selected variant, the quantity and matching
 * units already in the cart, and ticks the dispatch countdown once a minute.
 * Informational only: it never changes inventory, prices, availability or the
 * cart. Replaces the ET-derived "Module 03" that never initialised under
 * Empire's markup (retired in product-pro.safe.js).
 */
(function () {
  'use strict';

  const core = window.PDPFulfilment;
  if (!core) return; // Keep the server-rendered rows.

  const configs = new WeakMap();
  let cartQuantities = new Map();
  let cartRequest = null;

  const text = (node, value) => {
    // Avoid redundant writes and repeated screen-reader announcements.
    if (node && node.textContent !== value) node.textContent = value;
  };
  const fill = (template, token, value) => String(template || '').split(`__${token}__`).join(String(value));

  function configuration(box) {
    if (!configs.has(box)) {
      try {
        configs.set(box, JSON.parse(box.querySelector('[data-bn-fulfilment-config]').textContent));
      } catch (error) {
        return null; // Keep the server-rendered rows.
      }
    }
    return configs.get(box);
  }

  // Empire records the chosen variant on <variant-selection variant="…"> first,
  // then mirrors it into the form's id field once the product JSON arrives.
  function selectedVariantId(box, form) {
    const selection = form && form.querySelector('[data-variant-selection]');
    return String(
      (selection && selection.getAttribute('variant')) ||
      (form && form.querySelector('[name="id"]') && form.querySelector('[name="id"]').value) ||
      box.dataset.selectedVariant
    );
  }

  function requestedQuantity(form) {
    const input = form && form.querySelector('[name="quantity"]');
    return Math.max(1, parseInt(input && input.value, 10) || 1);
  }

  function render(variantId) {
    for (const box of document.querySelectorAll('[data-bn-fulfilment]')) {
      const config = configuration(box);
      if (!config) continue;
      const form = box.closest('form');
      // A variant event from another product form must not repaint this panel.
      if (variantId && !config.variants.some(item => String(item.id) === String(variantId))) continue;

      const id = String(variantId || selectedVariantId(box, form));
      const variant = config.variants.find(item => String(item.id) === id);
      const inCart = cartQuantities.get(id) || 0;
      const state = core.stockState(variant, requestedQuantity(form) + inCart, Number(box.dataset.threshold) || 0);
      const copy = config.copy;

      let kind = state.kind;
      if (kind === 'unavailable') kind = variant ? 'sold_out' : 'unavailable';
      let stock = copy[kind] || copy.untracked;
      let dispatch = '';

      if (kind === 'low' || kind === 'limited') stock = fill(stock, 'COUNT', state.qty);
      if (kind === 'supplier') {
        stock = state.mixed ? fill(copy.mixed, 'COUNT', state.qty) : copy.supplier;
        dispatch = state.uncertain ? copy.uncertain : copy.supplier_time;
      } else if (kind === 'stocked' || kind === 'low') {
        const schedule = core.dispatchState(new Date(), box.dataset.cutoff, box.dataset.closures, box.dataset.timezone);
        dispatch = schedule.kind === 'today' ? fill(copy.today, 'TIME', schedule.time)
          : schedule.kind === 'next' ? fill(copy.next, 'DATE', schedule.date)
          : copy.fallback;
      } else if (kind === 'untracked') {
        dispatch = copy.fallback;
      } else if (kind === 'limited') {
        dispatch = inCart > 0 ? copy.reduce_cart : copy.reduce;
      }

      box.dataset.state = kind;
      text(box.querySelector('[data-bn-fulfilment-stock]'), stock);
      text(box.querySelector('[data-bn-fulfilment-dispatch]'), dispatch);
      box.querySelector('[data-bn-fulfilment-dispatch-row]').hidden = !dispatch;
    }
  }

  // Read-only cart lookup so the quantity check counts units already in the
  // basket. Requests are de-duplicated; a failure leaves the panel as it is.
  function refreshCart() {
    if (cartRequest) return cartRequest;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    cartRequest = fetch(`${root}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(response => { if (!response.ok) throw new Error(`cart ${response.status}`); return response.json(); })
      .then(cart => {
        const next = new Map();
        for (const item of cart.items || []) {
          const key = String(item.variant_id);
          next.set(key, (next.get(key) || 0) + Number(item.quantity || 0));
        }
        cartQuantities = next;
      })
      .catch(() => {})
      .finally(() => { cartRequest = null; render(); });
    return cartRequest;
  }

  function boot() {
    if (!document.querySelector('[data-bn-fulfilment]')) return;
    render();
    refreshCart();
  }

  // Empire dispatches a non-bubbling "variant-change" on <variant-selection>;
  // the capture phase still sees it, before Empire's own handlers run.
  document.addEventListener('variant-change', event => {
    const variant = event.detail && event.detail.variant;
    render(variant && variant.id);
  }, true);

  // Typed quantities fire input/change; Empire's +/- buttons set the value
  // without an event, so re-read it on the next frame after a click.
  for (const type of ['input', 'change']) {
    document.addEventListener(type, event => {
      if (event.target.matches && event.target.matches('form [name="quantity"], form select[name="id"]')) render();
    });
  }
  document.addEventListener('click', event => {
    if (event.target.closest && event.target.closest('[data-quantity-plus], [data-quantity-minus]')) {
      requestAnimationFrame(() => render());
    }
  });

  // BN's cart drawer fires cart:updated on document; Empire fires
  // cartcount:update on window after an add to cart.
  document.addEventListener('cart:updated', refreshCart);
  window.addEventListener('cartcount:update', refreshCart);

  // Countdown: tick each minute while visible, and catch up on return.
  setInterval(() => { if (document.visibilityState === 'visible') render(); }, 60000);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') render(); });
  document.addEventListener('shopify:section:load', boot);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
